import type {
  DashboardPropertyFilters,
  DashboardPropertyVideoFilter,
  DashboardPropertyVisibilityFilter,
  TagId,
} from "../../../convex/types";

export type PropertyFilterChip = {
  key: string;
  label: string;
};

export type PropertyListingAgentOption = {
  agentId: string;
  agentName: string;
  propertyCount: number;
};

export type PropertyTypeOption = {
  value: string;
  label: string;
};

export const PROPERTY_TYPE_OPTIONS: PropertyTypeOption[] = [
  { value: "freehold_properties", label: "Freehold Properties" },
  { value: "freehold_villas", label: "Freehold Villas" },
  { value: "freehold_lands", label: "Freehold Land" },
  { value: "leasehold_lands", label: "Leasehold Land" },
  { value: "leasehold_villas", label: "Leasehold Villas" },
  { value: "rentals", label: "Rentals" },
  { value: "freehold_commercial", label: "Freehold Commercial" },
  { value: "leasehold_commercial", label: "Leasehold Commercial" },
];

export const PROPERTY_STATUS_OPTIONS = [
  { value: "Available", label: "Available" },
  { value: "Not Available", label: "Not available" },
  { value: "Under Construction", label: "Under construction" },
  { value: "Ready", label: "Ready" },
] as const;

export const PROPERTY_VIDEO_OPTIONS: Array<{
  value: DashboardPropertyVideoFilter;
  label: string;
}> = [
  { value: "all", label: "All properties" },
  { value: "withVideo", label: "Has video thumbnail" },
  { value: "withoutVideo", label: "No video thumbnail" },
];

export const PROPERTY_VISIBILITY_OPTIONS: Array<{
  value: DashboardPropertyVisibilityFilter;
  label: string;
}> = [
  { value: "all", label: "All properties" },
  { value: "published", label: "Published" },
  { value: "hidden", label: "Hidden" },
  { value: "draft", label: "Draft" },
];

export const DEFAULT_DASHBOARD_PROPERTY_FILTERS: DashboardPropertyFilters = {
  searchQuery: "",
  tagIds: [],
  propertyTypes: [],
  status: "",
  bedroomMin: null,
  bedroomMax: null,
  bathroomMin: null,
  bathroomMax: null,
  buildSizeMin: null,
  buildSizeMax: null,
  landSizeMin: null,
  landSizeMax: null,
  yearBuiltMin: null,
  yearBuiltMax: null,
  priceMin: null,
  priceMax: null,
  hasVideoThumbnail: "all",
  visibility: "all",
  listingAgentKeys: [],
};

/**
 * Parses dashboard filter state from the current query string.
 */
export function parseDashboardPropertyFilters(
  search: string,
): DashboardPropertyFilters {
  const params = new URLSearchParams(search);

  return {
    searchQuery: params.get("q") || "",
    tagIds: readStringArray(params, "tags") as TagId[],
    propertyTypes: readStringArray(params, "types"),
    status: params.get("status") || "",
    bedroomMin: readNullableNumber(params, "bedMin"),
    bedroomMax: readNullableNumber(params, "bedMax"),
    bathroomMin: readNullableNumber(params, "bathMin"),
    bathroomMax: readNullableNumber(params, "bathMax"),
    buildSizeMin: readNullableNumber(params, "buildMin"),
    buildSizeMax: readNullableNumber(params, "buildMax"),
    landSizeMin: readNullableNumber(params, "landMin"),
    landSizeMax: readNullableNumber(params, "landMax"),
    yearBuiltMin: readNullableNumber(params, "yearMin"),
    yearBuiltMax: readNullableNumber(params, "yearMax"),
    priceMin: readNullableNumber(params, "priceMin"),
    priceMax: readNullableNumber(params, "priceMax"),
    hasVideoThumbnail: readVideoFilter(params.get("video")),
    visibility: readVisibilityFilter(params.get("visibility")),
    listingAgentKeys: uniqueNonEmptyStrings(
      readStringArray(params, "listingAgent"),
    ),
  };
}

/**
 * Serializes dashboard filter state into URL query params.
 */
export function buildDashboardPropertySearchParams(
  filters: DashboardPropertyFilters,
): URLSearchParams {
  const params = new URLSearchParams();

  setStringParam(params, "q", filters.searchQuery);
  setArrayParam(params, "tags", filters.tagIds);
  setArrayParam(params, "types", filters.propertyTypes);
  setStringParam(params, "status", filters.status);
  setNumberParam(params, "bedMin", filters.bedroomMin);
  setNumberParam(params, "bedMax", filters.bedroomMax);
  setNumberParam(params, "bathMin", filters.bathroomMin);
  setNumberParam(params, "bathMax", filters.bathroomMax);
  setNumberParam(params, "buildMin", filters.buildSizeMin);
  setNumberParam(params, "buildMax", filters.buildSizeMax);
  setNumberParam(params, "landMin", filters.landSizeMin);
  setNumberParam(params, "landMax", filters.landSizeMax);
  setNumberParam(params, "yearMin", filters.yearBuiltMin);
  setNumberParam(params, "yearMax", filters.yearBuiltMax);
  setNumberParam(params, "priceMin", filters.priceMin);
  setNumberParam(params, "priceMax", filters.priceMax);

  if (filters.hasVideoThumbnail !== "all") {
    params.set("video", filters.hasVideoThumbnail);
  }

  if (filters.visibility !== "all") {
    params.set("visibility", filters.visibility);
  }

  setArrayParam(params, "listingAgent", filters.listingAgentKeys);

  return params;
}

export function countActiveDashboardPropertyFilters(
  filters: DashboardPropertyFilters,
): number {
  return getDashboardPropertyFilterChips(filters, new Map()).length;
}

export function areDashboardPropertyFiltersEqual(
  left: DashboardPropertyFilters,
  right: DashboardPropertyFilters,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function getDashboardPropertyFilterChips(
  filters: DashboardPropertyFilters,
  tagLabels: Map<string, string>,
  listingAgentLabels: Map<string, string> = new Map(),
): PropertyFilterChip[] {
  const chips: PropertyFilterChip[] = [];

  if (filters.searchQuery.trim() !== "") {
    chips.push({
      key: "searchQuery",
      label: `Search: ${filters.searchQuery.trim()}`,
    });
  }

  for (const tagId of filters.tagIds) {
    const tagLabel = tagLabels.get(tagId) || "Tag";
    chips.push({
      key: `tag:${tagId}`,
      label: `Tag: ${tagLabel}`,
    });
  }

  for (const propertyType of filters.propertyTypes) {
    chips.push({
      key: `type:${propertyType}`,
      label: `Type: ${getPropertyTypeLabel(propertyType)}`,
    });
  }

  if (filters.status !== "") {
    chips.push({
      key: "status",
      label: `Status: ${filters.status}`,
    });
  }

  for (const agentKey of filters.listingAgentKeys) {
    const trimmedKey = agentKey.trim();
    if (trimmedKey === "") {
      continue;
    }
    const agentLabel =
      listingAgentLabels.get(trimmedKey) || `Listing agent: ${trimmedKey}`;
    chips.push({
      key: `listingAgent:${trimmedKey}`,
      label: agentLabel,
    });
  }

  pushRangeChip(
    chips,
    "Bedrooms",
    "bedrooms",
    filters.bedroomMin,
    filters.bedroomMax,
  );
  pushRangeChip(
    chips,
    "Bathrooms",
    "bathrooms",
    filters.bathroomMin,
    filters.bathroomMax,
  );
  pushRangeChip(
    chips,
    "Build size",
    "build",
    filters.buildSizeMin,
    filters.buildSizeMax,
    " sqm",
  );
  pushRangeChip(
    chips,
    "Land size",
    "land",
    filters.landSizeMin,
    filters.landSizeMax,
    " sqm",
  );
  pushRangeChip(
    chips,
    "Year built",
    "year",
    filters.yearBuiltMin,
    filters.yearBuiltMax,
  );
  pushRangeChip(
    chips,
    "Total price",
    "price",
    filters.priceMin,
    filters.priceMax,
  );

  if (filters.hasVideoThumbnail === "withVideo") {
    chips.push({
      key: "video",
      label: "Has video thumbnail",
    });
  }

  if (filters.hasVideoThumbnail === "withoutVideo") {
    chips.push({
      key: "video",
      label: "No video thumbnail",
    });
  }

  if (filters.visibility !== "all") {
    chips.push({
      key: "visibility",
      label: `Visibility: ${getVisibilityLabel(filters.visibility)}`,
    });
  }

  return chips;
}

export function clearDashboardPropertyFilter(
  filters: DashboardPropertyFilters,
  chipKey: string,
): DashboardPropertyFilters {
  if (chipKey === "searchQuery") {
    return {
      ...filters,
      searchQuery: "",
    };
  }

  if (chipKey === "status") {
    return {
      ...filters,
      status: "",
    };
  }

  if (chipKey === "video") {
    return {
      ...filters,
      hasVideoThumbnail: "all",
    };
  }

  if (chipKey === "visibility") {
    return {
      ...filters,
      visibility: "all",
    };
  }

  if (chipKey.startsWith("listingAgent:")) {
    const agentKey = chipKey.slice("listingAgent:".length);
    return {
      ...filters,
      listingAgentKeys: filters.listingAgentKeys.filter(
        (currentKey) => currentKey.trim() !== agentKey.trim(),
      ),
    };
  }

  if (chipKey.startsWith("tag:")) {
    const tagId = chipKey.slice(4);
    return {
      ...filters,
      tagIds: filters.tagIds.filter((currentTagId) => currentTagId !== tagId),
    };
  }

  if (chipKey.startsWith("type:")) {
    const propertyType = chipKey.slice(5);
    return {
      ...filters,
      propertyTypes: filters.propertyTypes.filter(
        (currentType) => currentType !== propertyType,
      ),
    };
  }

  if (chipKey === "bedrooms") {
    return {
      ...filters,
      bedroomMin: null,
      bedroomMax: null,
    };
  }

  if (chipKey === "bathrooms") {
    return {
      ...filters,
      bathroomMin: null,
      bathroomMax: null,
    };
  }

  if (chipKey === "build") {
    return {
      ...filters,
      buildSizeMin: null,
      buildSizeMax: null,
    };
  }

  if (chipKey === "land") {
    return {
      ...filters,
      landSizeMin: null,
      landSizeMax: null,
    };
  }

  if (chipKey === "year") {
    return {
      ...filters,
      yearBuiltMin: null,
      yearBuiltMax: null,
    };
  }

  if (chipKey === "price") {
    return {
      ...filters,
      priceMin: null,
      priceMax: null,
    };
  }

  return filters;
}

export function hasAnyDashboardPropertyFilter(
  filters: DashboardPropertyFilters,
): boolean {
  return countActiveDashboardPropertyFilters(filters) > 0;
}

export function getPropertyTypeLabel(value: string): string {
  const match = PROPERTY_TYPE_OPTIONS.find((option) => option.value === value);
  if (match) {
    return match.label;
  }

  return value
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function readStringArray(params: URLSearchParams, key: string): string[] {
  const rawValue = params.get(key);
  if (!rawValue) {
    return [];
  }

  return rawValue
    .split(",")
    .map((value) => value.trim())
    .filter((value) => value !== "");
}

export function uniqueNonEmptyStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (trimmed === "" || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    result.push(trimmed);
  }
  return result;
}

function readNullableNumber(
  params: URLSearchParams,
  key: string,
): number | null {
  const rawValue = params.get(key);
  if (!rawValue) {
    return null;
  }

  const parsedValue = Number(rawValue);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  return parsedValue;
}

function readVideoFilter(
  rawValue: string | null,
): DashboardPropertyVideoFilter {
  if (rawValue === "withVideo" || rawValue === "withoutVideo") {
    return rawValue;
  }

  return "all";
}

function readVisibilityFilter(
  rawValue: string | null,
): DashboardPropertyVisibilityFilter {
  if (
    rawValue === "published" ||
    rawValue === "hidden" ||
    rawValue === "draft"
  ) {
    return rawValue;
  }

  return "all";
}

function getVisibilityLabel(value: DashboardPropertyVisibilityFilter): string {
  const match = PROPERTY_VISIBILITY_OPTIONS.find(
    (option) => option.value === value,
  );
  return match ? match.label : value;
}

function setStringParam(
  params: URLSearchParams,
  key: string,
  value: string,
): void {
  if (value.trim() !== "") {
    params.set(key, value.trim());
  }
}

function setArrayParam(
  params: URLSearchParams,
  key: string,
  values: string[],
): void {
  if (values.length > 0) {
    params.set(key, values.join(","));
  }
}

function setNumberParam(
  params: URLSearchParams,
  key: string,
  value: number | null,
): void {
  if (value !== null) {
    params.set(key, value.toString());
  }
}

function pushRangeChip(
  chips: PropertyFilterChip[],
  label: string,
  key: string,
  minValue: number | null,
  maxValue: number | null,
  suffix = "",
): void {
  if (minValue === null && maxValue === null) {
    return;
  }

  if (minValue !== null && maxValue !== null) {
    chips.push({
      key,
      label: `${label}: ${formatNumber(minValue)}-${formatNumber(maxValue)}${suffix}`,
    });
    return;
  }

  if (minValue !== null) {
    chips.push({
      key,
      label: `${label}: ${formatNumber(minValue)}+${suffix}`,
    });
    return;
  }

  chips.push({
    key,
    label: `${label}: Up to ${formatNumber(maxValue as number)}${suffix}`,
  });
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat().format(value);
}
